import { IGetDetailsConsultant } from "../core/_models"
import { useState } from "react"
import swal from "sweetalert2"
import { toast } from "react-toastify"
import axios from "axios"
import {
     CONST_API_BUSINESS_CONSULTANT_SET_CODE_ANALYTIQUE_ENDPOINT,
     CONST_API_BUSINESS_CONSULTANT_SET_IS_CONCERNED_BY_TT_ENDPOINT,
     CONST_API_BUSINESS_CONSULTANT_SET_NUMERO_IMMAT_CEGEDIM_ENDPOINT,
} from "../../../../../endpoints"
import { REACT_QUERY_KEY_GET_CONSULTANT_DETAILS } from "../ConsultantDetails"
import { useQueryClient } from "react-query"

const ConsultantDetailsInfoBaseInfoContract = ({ consultantInfoPerso }: IConsultantDetailsInfoBaseInfoContractPropsModel) => {
     const queryClient = useQueryClient()
     const [isCodeAnalytiqueCopied, setIsCodeAnalytiqueCopied] = useState(false)
     const [isNumeroImmatCEGEDIMCopied, setIsNumeroImmatCEGEDIMCopied] = useState(false)
     const [isUpdatingTeletravail, setIsUpdatingTeletravail] = useState(false)

     const contratLabel = consultantInfoPerso.detailsDuJour?.contratLabel || consultantInfoPerso.consultant.contratLabel
     const statutLabel = consultantInfoPerso.detailsDuJour?.statutLabel || consultantInfoPerso.consultant.statutLabel
     const metier = consultantInfoPerso.detailsDuJour?.metier || consultantInfoPerso.consultant.metier
     const salaireOuTJMST = consultantInfoPerso.detailsDuJour?.coutBrutNonCharge || consultantInfoPerso.consultant.coutBrutNonCharge
     const heuresTravail = consultantInfoPerso.detailsDuJour?.heures_travail || consultantInfoPerso.consultant.heuresTravail
     const heuresTravailSupp = consultantInfoPerso.detailsDuJour?.heures_travail_supp || consultantInfoPerso.consultant.heuresTravailSupp
     const entiteRattachement = consultantInfoPerso.detailsDuJour?.entite || consultantInfoPerso.consultant.entite
     const entiteFacturation = consultantInfoPerso.detailsDuJour?.entiteFacturation || consultantInfoPerso.consultant.entiteFacturation

     const handleCopyCodeAnalytique = async (code: string) => {
          try {
               await navigator.clipboard.writeText(code)
               setIsCodeAnalytiqueCopied(true)
               setTimeout(() => setIsCodeAnalytiqueCopied(false), 1000) // reset after 1s
          } catch (err) {
               console.error("Failed to copy:", err)
          }
     }

     const handleCopyNumeroCEGEDIM = async (code: string) => {
          try {
               await navigator.clipboard.writeText(code)
               setIsNumeroImmatCEGEDIMCopied(true)
               setTimeout(() => setIsNumeroImmatCEGEDIMCopied(false), 1000) // reset after 1s
          } catch (err) {
               console.error("Failed to copy:", err)
          }
     }

     function handleEditCodeAnalytique() {
          swal.fire({
               title: "Enter le nouveau code analytique",
               input: "text",
               inputLabel: "Code analytique",
               inputValue: consultantInfoPerso.consultant.codeAnalytique || "",
               showCancelButton: true,
               confirmButtonText: "Confirmer",
               cancelButtonText: "Annuler",
               showLoaderOnConfirm: true,
               inputPlaceholder: "Merci de préciser la raison de ce rejet",
               inputAttributes: {
                    required: "true",
               },
               inputValidator: value => {
                    if (!value) {
                         return "Le nouveau code est requis"
                    }
               },
               preConfirm: async (codeAnalytique: string) => {
                    return axios
                         .post(CONST_API_BUSINESS_CONSULTANT_SET_CODE_ANALYTIQUE_ENDPOINT(consultantInfoPerso.consultant.id!), {
                              codeAnalytique,
                         })
                         .then(() => {
                              queryClient.setQueryData<IGetDetailsConsultant | undefined>(REACT_QUERY_KEY_GET_CONSULTANT_DETAILS, (prev: IGetDetailsConsultant | undefined) => {
                                   if (prev) {
                                        prev.consultant = {
                                             ...prev.consultant,
                                             codeAnalytique,
                                        }
                                   }

                                   return prev
                              })
                              toast.success("Le nouveau code analytique a bien été pris en compte.")
                         })
                         .catch(e => {
                              toast.error(e?.response?.data?.detail, { autoClose: false })
                         })
               },
               allowOutsideClick: () => !swal.isLoading(),
          }).then()
     }

     function handleEditNumeroImmat() {
          swal.fire({
               title: "Enter le n° immat. CEGEDIM",
               input: "text",
               inputLabel: "N° immat. CEGEDIM",
               inputValue: consultantInfoPerso.consultant.CEGEDIM_mmat || "",
               showCancelButton: true,
               confirmButtonText: "Confirmer",
               cancelButtonText: "Annuler",
               showLoaderOnConfirm: true,
               inputPlaceholder: "N° immat. CEGEDIM",
               inputAttributes: {
                    required: "true",
               },
               inputValidator: value => {
                    if (!value) {
                         return "Le n° immat. CEGEDIM est requis"
                    }
               },
               preConfirm: async (numero: string) => {
                    return axios
                         .post(CONST_API_BUSINESS_CONSULTANT_SET_NUMERO_IMMAT_CEGEDIM_ENDPOINT(consultantInfoPerso.consultant.id!), {
                              numero,
                         })
                         .then(() => {
                              queryClient.setQueryData<IGetDetailsConsultant | undefined>(REACT_QUERY_KEY_GET_CONSULTANT_DETAILS, (prev: IGetDetailsConsultant | undefined) => {
                                   if (prev) {
                                        prev.consultant = {
                                             ...prev.consultant,
                                             CEGEDIM_mmat: numero,
                                        }
                                   }

                                   return prev
                              })
                              toast.success("Le nouveau n° d'immatriculation a bien été pris en compte.")
                         })
                         .catch(e => {
                              toast.error(e?.response?.data?.detail, { autoClose: false })
                         })
               },
               allowOutsideClick: () => !swal.isLoading(),
          }).then()
     }

     async function handleSetIsConcernedByTT(isConcernedByTeletravail: boolean) {
          try {
               setIsUpdatingTeletravail(true)

               await axios.post(CONST_API_BUSINESS_CONSULTANT_SET_IS_CONCERNED_BY_TT_ENDPOINT(consultantInfoPerso.consultant.id!), { isConcernedByTeletravail })

               queryClient.setQueryData<IGetDetailsConsultant | undefined>(REACT_QUERY_KEY_GET_CONSULTANT_DETAILS, prev => {
                    if (prev) {
                         prev.consultant = {
                              ...prev.consultant,
                              isConsultantConcernedByTT: isConcernedByTeletravail,
                         }
                    }
                    return prev
               })
          } catch (e: any) {
               toast.error(e?.response?.data?.detail, { autoClose: false })
          } finally {
               setIsUpdatingTeletravail(false)
          }
     }

     return (
          <>
               {/* Date entrée */}
               <div className="row mb-7">
                    <label className="col-lg-4 fw-bold text-muted">{consultantInfoPerso.consultant.contrat === "CONTRAT_ST" ? "Date d'entrée" : "Date d'embauche"}</label>
                    <div className="col-lg-8">
                         <span className="fw-bolder fs-6 text-dark">Le {consultantInfoPerso.consultant.dateEmbauche?.format("dddd D MMMM YYYY")}</span>
                    </div>
               </div>

               {/* Date sortie */}
               {consultantInfoPerso.consultant.dateSortie && (
                    <div className="row mb-7">
                         <label className="col-lg-4 fw-bold text-muted">Date de sortie</label>
                         <div className="col-lg-8">
                              <span className="fw-bolder fs-6 text-dark">Le {consultantInfoPerso.consultant.dateSortie?.format("dddd D MMMM YYYY")}</span>
                         </div>
                    </div>
               )}

               {/* Statut */}
               <div className="row mb-7">
                    <label className="col-lg-4 fw-bold text-muted">Statut</label>
                    <div className="col-lg-8">
                         <span className="fw-bolder fs-6 text-dark">{statutLabel}</span>
                    </div>
               </div>

               {/* Contrat */}
               <div className="row mb-7">
                    <label className="col-lg-4 fw-bold text-muted">Contrat</label>
                    <div className="col-lg-8">
                         <span className="fw-bolder fs-6 text-dark">{contratLabel}</span>
                    </div>
               </div>

               {/* Métier */}
               <div className="row mb-7">
                    <label className="col-lg-4 fw-bold text-muted">Métier</label>
                    <div className="col-lg-8">
                         <span className="fw-bolder fs-6 text-dark">{metier?.nom}</span>
                    </div>
               </div>

               {/* Salaire / TJMST */}
               {!consultantInfoPerso.isConnectedUserWithDelegationAdministrative && (
                    <div className="row mb-7">
                         <label className="col-lg-4 fw-bold text-muted">
                              {consultantInfoPerso.consultant.contrat === "CONTRAT_ST" ? "Taux d'achat journalier" : "Salaire brut base 35€"}
                         </label>
                         <div className="col-lg-8">
                              <span className="fw-bolder fs-6 text-dark">
                                   {consultantInfoPerso.consultant.contrat === "CONTRAT_ST" ? `${salaireOuTJMST}€ HT / jour` : `${salaireOuTJMST}€ / mois`}
                              </span>
                         </div>
                    </div>
               )}

               {consultantInfoPerso.consultant.contrat !== "CONTRAT_ST" && (
                    <>
                         {/* Heures & Heures supp */}
                         <div className="row mb-7">
                              <label className="col-lg-4 fw-bold text-muted">Heures de travail</label>
                              <div className="col-lg-8">
                                   <span className="fw-bolder fs-6 text-dark">{heuresTravail}h / jour</span>
                              </div>
                         </div>
                         {/* Heures & Heures supp */}
                         <div className="row mb-7">
                              <label className="col-lg-4 fw-bold text-muted">Heures supp.</label>
                              <div className="col-lg-8">
                                   <span className="fw-bolder fs-6 text-dark">{heuresTravailSupp}h / jour</span>
                              </div>
                         </div>
                    </>
               )}

               {/* Entité de rattachement */}
               <div className="row mb-7">
                    <label className="col-lg-4 fw-bold text-muted">Entité de rattachement</label>
                    <div className="col-lg-8">
                         <span className="fw-bolder fs-6 text-dark">{entiteRattachement?.nom}</span>
                    </div>
               </div>

               {/* Entité de facturation */}
               <div className="row mb-7">
                    <label className="col-lg-4 fw-bold text-muted">Entité de facturation</label>
                    <div className="col-lg-8">
                         <span className="fw-bolder fs-6 text-dark">{entiteFacturation?.nom}</span>
                    </div>
               </div>

               {/* Code analytique */}
               <div className="row mb-7">
                    <label className="col-lg-4 fw-bold text-muted">Code analytique</label>
                    <div className="col-lg-8">
                         <span className="fw-bolder fs-6 text-dark me-2">{consultantInfoPerso.consultant.codeAnalytique}</span>
                         <span
                              onClick={() => handleCopyCodeAnalytique(consultantInfoPerso.consultant.codeAnalytique || "")}
                              style={{ background: "none", border: "none", cursor: "pointer" }}
                         >
                              {isCodeAnalytiqueCopied ? <i className="fas fa-check fs-3 text-success" /> : <i className="fas fa-copy fs-3 text-primary" title={"Copier"} />}
                         </span>
                         <i className="fas fa-edit fs-3 text-warning ms-2 cursor-pointer" title={"Editer"} onClick={handleEditCodeAnalytique} />
                    </div>
               </div>

               {/* Is concerned By TT */}
               {consultantInfoPerso.consultant.contrat !== "CONTRAT_ST" && (
                    <>
                         {/* N° immatriculation CEGEDIM */}
                         <div className="row mb-7">
                              <label className="col-lg-4 fw-bold text-muted">N° immat. CEGEDIM</label>
                              <div className="col-lg-8">
                                   {consultantInfoPerso.consultant.CEGEDIM_mmat ? (
                                        <>
                                             <span className="fw-bolder fs-6 text-dark me-2">{consultantInfoPerso.consultant.CEGEDIM_mmat}</span>
                                             <span
                                                  onClick={() => handleCopyNumeroCEGEDIM(consultantInfoPerso.consultant.CEGEDIM_mmat || "")}
                                                  style={{ background: "none", border: "none", cursor: "pointer" }}
                                             >
                                                  {isNumeroImmatCEGEDIMCopied ? (
                                                       <i className="fas fa-check fs-3 text-success" />
                                                  ) : (
                                                       <i className="fas fa-copy fs-3 text-primary" title={"Copier"} />
                                                  )}
                                             </span>
                                        </>
                                   ) : (
                                        <i className="fas fa-edit fs-3 text-warning cursor-pointer" title={"Editer"} onClick={handleEditNumeroImmat} />
                                   )}
                              </div>
                         </div>

                         <div className="row mb-7">
                              <label className="col-lg-4 fw-bold text-muted">
                                   Est-{consultantInfoPerso.consultant.civilite === "CIVILITE_MR" ? "il" : "elle"} concerné par le télétravail?
                              </label>
                              <div className="col-lg-8">
                                   <div className={"form-check form-check-custom form-check-solid"}>
                                        <input
                                             type="checkbox"
                                             className="form-check-input cursor-pointer border border-secondary"
                                             checked={consultantInfoPerso.consultant.isConsultantConcernedByTT}
                                             onChange={val => handleSetIsConcernedByTT(val.target.checked)}
                                             disabled={isUpdatingTeletravail}
                                        />
                                   </div>
                              </div>
                         </div>
                    </>
               )}
          </>
     )
}

interface IConsultantDetailsInfoBaseInfoContractPropsModel {
     consultantInfoPerso: IGetDetailsConsultant
}

export default ConsultantDetailsInfoBaseInfoContract
